<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import axios from "axios";
import type { TournamentState, TournamentRoster } from "@interfaces/tournament_type";
import TournamentLeaderboard from "./TournamentLeaderboard.vue";

interface Props {
  eventId: number;
}

const props = defineProps<Props>();

const tournamentState = ref<TournamentState | null>(null);
const isLoading = ref(true);
const error = ref<string | null>(null);

const { t } = useI18n();

const loadTournamentState = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const response = await axios.get(
      `/api/v1/events/tournament/state?event_id=${props.eventId}`
    );
    tournamentState.value = response.data;
  } catch (err) {
    console.error("Error loading tournament state:", err);
    error.value = t("tournament.results.error_loading");
  } finally {
    isLoading.value = false;
  }
};

const isTournamentStarted = computed(() => {
  return tournamentState.value && tournamentState.value.current_round > 0;
});

const isTournamentFinished = computed(() => {
  return tournamentState.value?.is_finished || false;
});

// Extract player rosters from matches for the leaderboard
const playerRosters = computed(() => {
  if (!tournamentState.value?.matches) return {};

  const rosters: Record<number, TournamentRoster[]> = {};

  tournamentState.value.matches.forEach((match) => {
    // Handle player1 rosters
    if (match.player1_rosters && match.player1_rosters.length > 0) {
      rosters[match.player1.id] = match.player1_rosters;
    } else if (match.player1_roster) {
      // Fallback to single roster
      rosters[match.player1.id] = [match.player1_roster];
    }

    // Handle player2 rosters
    if (match.player2_rosters && match.player2_rosters.length > 0) {
      rosters[match.player2.id] = match.player2_rosters;
    } else if (match.player2_roster) {
      // Fallback to single roster
      rosters[match.player2.id] = [match.player2_roster];
    }
  });

  return rosters;
});

onMounted(() => {
  loadTournamentState();
});
</script>

<template>
  <div class="tournament-results">
    <h3>{{ t("tournament.results.title") }}</h3>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <div v-if="isLoading" class="loading">{{ t("tournament.results.loading") }}</div>

    <div v-else-if="!tournamentState" class="no-tournament">
      {{ t("tournament.results.no_tournament_state") }}
    </div>

    <div v-else-if="!isTournamentStarted" class="not-started">
      <p>{{ t("tournament.results.not_started_line1") }}</p>
      <p>{{ t("tournament.results.not_started_line2") }}</p>
    </div>

    <div v-else class="results-content">
      <!-- Tournament Info -->
      <div class="tournament-info">
        <div class="info-item">
          <span class="label">{{ t("tournament.results.participants_label") }}:</span>
          <span class="value">{{ tournamentState.approved_participants }}</span>
        </div>
        <div class="info-item">
          <span class="label">{{ t("tournament.results.current_round_label") }}:</span>
          <span class="value">{{
            t("tournament.results.current_round_of", {
              current: tournamentState.current_round,
              total: tournamentState.total_rounds,
            })
          }}</span>
        </div>
        <div v-if="isTournamentFinished" class="info-item">
          <span class="label">{{ t("tournament.results.status_label") }}:</span>
          <span class="value finished">{{ t("tournament.status.finished") }}</span>
        </div>
        <div v-else class="info-item">
          <span class="label">{{ t("tournament.results.status_label") }}:</span>
          <span class="value ongoing">{{ t("tournament.status.in_progress") }}</span>
        </div>
      </div>

      <!-- Tournament Leaderboard -->
      <TournamentLeaderboard
        :standings="tournamentState.standings"
        :is-finished="isTournamentFinished"
        :show-faction-links="true"
        :player-rosters="playerRosters"
        :current-user-id="tournamentState.current_user_id"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tournament-results {
  padding: 20px;
  background-color: #efd8b0;
  border-radius: 8px;
  border: 1px solid #bd9c65;
}

h3 {
  color: #60451f;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: bold;
}

.error-message {
  background-color: #ffebee;
  color: #c62828;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
  border: 1px solid #ffcdd2;
}

.loading,
.no-tournament,
.not-started {
  text-align: center;
  color: #bd9c65;
  padding: 20px;
}

.results-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.tournament-info {
  display: flex;
  gap: 20px;
  padding: 15px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #bd9c65;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.label {
  font-size: 12px;
  color: #bd9c65;
}

.value {
  font-weight: bold;
  color: #60451f;
  font-size: 16px;

  &.finished {
    color: #4caf50;
  }

  &.ongoing {
    color: #ff9800;
  }
}

// Responsive design
@media (max-width: 768px) {
  .tournament-results {
    padding: 15px;
  }

  h3 {
    font-size: 16px;
    margin-bottom: 15px;
  }

  .tournament-info {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }

  .info-item {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .label {
    font-size: 13px;
  }

  .value {
    font-size: 14px;
  }

  .results-content {
    gap: 15px;
  }
}

@media (max-width: 480px) {
  .tournament-results {
    padding: 10px;
  }

  h3 {
    font-size: 15px;
    margin-bottom: 12px;
  }

  .tournament-info {
    padding: 10px;
    gap: 10px;
  }

  .label {
    font-size: 12px;
  }

  .value {
    font-size: 13px;
  }
}
</style>
